import React, { useCallback, useMemo, useState } from 'react';
import { RowDragEvent } from 'ag-grid-community';
import { useMutation } from '@apollo/react-hooks';
import AdminList from '../../../../../ui/admin/AdminList';
import { Channel, ChannelGroup } from '../../../../../../shared/types';
import { CHANNEL_ORDER_ALL_KEY } from '../../../../../../shared/channelOrder';
import OrderedGroupChips, { ALL_GROUPS } from '../OrderedGroupChips';
import Footer from '../Footer';
import channelOrderingColumnDefs from './columnDefs';
import { Box } from '@mui/material';
import { REORDER_CHANNELS_IN_GROUPS } from '../../../../../../gql/channelGroups';
import {
    MessageTypes,
    useNotification,
} from '../../../../../providers/NotificationProvider';
import { useSelectedGroupId } from '../../hooks/useSelectedGroupId';
import {
    sortByPersistedOrder,
    sortByExplicitOrder,
} from '../../utils/channelSortUtils';

interface ChannelOrderingTableProps {
    channels: Channel[];
    channelGroups: ChannelGroup[];
    loading: boolean;
    onRefetch: () => void;
}

const ChannelOrderingTable: React.FC<ChannelOrderingTableProps> = ({
    channels,
    channelGroups,
    loading,
    onRefetch,
}) => {
    const { showMessage } = useNotification();
    const { selectedGroupId, handleSelectGroup, getDragSelectedGroupId } =
        useSelectedGroupId();

    const [pendingChannelOrders, setPendingChannelOrders] = useState<
        Record<string, string[]>
    >({});
    const [reorderChannelsInGroups, { loading: saving }] = useMutation(
        REORDER_CHANNELS_IN_GROUPS
    );

    const selectedChannelGroup = useMemo(
        () =>
            channelGroups.find(
                (group) => String(group._id) === String(selectedGroupId)
            ) || null,
        [channelGroups, selectedGroupId]
    );

    const getPersistedOrderForScope = useCallback(
        (groupId: string): string[] => {
            if (groupId === ALL_GROUPS) {
                return sortByPersistedOrder(
                    channels,
                    CHANNEL_ORDER_ALL_KEY
                ).map((channel) => String(channel._id));
            }

            const group = channelGroups.find(
                (item) => String(item._id) === String(groupId)
            );
            const groupChannelIds = new Set(group?.channelIds || []);
            const channelsInGroup = channels.filter((channel) =>
                groupChannelIds.has(String(channel._id))
            );

            return sortByPersistedOrder(channelsInGroup, groupId).map(
                (channel) => String(channel._id)
            );
        },
        [channels, channelGroups]
    );

    const areSameOrder = useCallback(
        (left: string[] = [], right: string[] = []): boolean => {
            if (left.length !== right.length) return false;
            return left.every((id, index) => id === right[index]);
        },
        []
    );

    const displayedChannels = useMemo(() => {
        const sortByPendingOrder = (channels: Channel[], groupId: string) => {
            const pendingOrder = pendingChannelOrders[groupId] || [];
            if (!pendingOrder.length) return null;
            return sortByExplicitOrder(channels, pendingOrder);
        };

        if (selectedGroupId === ALL_GROUPS) {
            return (
                sortByPendingOrder(channels, ALL_GROUPS) ||
                sortByPersistedOrder(channels, CHANNEL_ORDER_ALL_KEY)
            );
        }

        const groupChannelIds = new Set(selectedChannelGroup?.channelIds || []);
        const channelsInGroup = channels.filter((channel) =>
            groupChannelIds.has(String(channel._id))
        );

        return (
            sortByPendingOrder(channelsInGroup, selectedGroupId) ||
            sortByPersistedOrder(channelsInGroup, selectedGroupId)
        );
    }, [channels, selectedChannelGroup, selectedGroupId, pendingChannelOrders]);

    const onRowDragEnd = useCallback(
        (event: RowDragEvent) => {
            const reorderedIds: string[] = [];
            event.api.forEachNodeAfterFilterAndSort((node) => {
                if (node.data?._id) reorderedIds.push(String(node.data._id));
            });
            const { scopeKey: groupId } = getDragSelectedGroupId();
            const persistedOrder = getPersistedOrderForScope(groupId);
            setPendingChannelOrders((prev) => {
                if (areSameOrder(reorderedIds, persistedOrder)) {
                    const nextPendingOrders = { ...prev };
                    delete nextPendingOrders[groupId];
                    return nextPendingOrders;
                }

                return {
                    ...prev,
                    [groupId]: reorderedIds,
                };
            });
        },
        [getDragSelectedGroupId, getPersistedOrderForScope, areSameOrder]
    );

    const onSave = useCallback(async () => {
        const changedOrders = Object.entries(pendingChannelOrders)
            .filter(
                ([groupId, channelIds]) =>
                    !areSameOrder(
                        channelIds,
                        getPersistedOrderForScope(groupId)
                    )
            )
            .map(([groupId, channelIds]) => ({
                groupId:
                    groupId === ALL_GROUPS ? CHANNEL_ORDER_ALL_KEY : groupId,
                channelIds,
            }));

        if (!changedOrders.length) {
            setPendingChannelOrders({});
            return;
        }

        try {
            await reorderChannelsInGroups({
                variables: { orders: changedOrders },
            });

            showMessage('Channel ordering has been saved.', MessageTypes.info);
            await onRefetch();
            setPendingChannelOrders({});
        } catch {
            showMessage(
                'Failed to save channel ordering. Please try again.',
                MessageTypes.error
            );
        }
    }, [
        pendingChannelOrders,
        reorderChannelsInGroups,
        onRefetch,
        showMessage,
        areSameOrder,
        getPersistedOrderForScope,
    ]);

    const hasPendingChanges = useMemo(() => {
        return Object.entries(pendingChannelOrders).some(
            ([groupId, channelIds]) => {
                return !areSameOrder(
                    channelIds,
                    getPersistedOrderForScope(groupId)
                );
            }
        );
    }, [pendingChannelOrders, areSameOrder, getPersistedOrderForScope]);

    return (
        <Box sx={{ mt: 2 }}>
            <AdminList
                title="Channel Order"
                name="channel-order"
                items={displayedChannels}
                loading={loading}
                aboveTable={
                    <OrderedGroupChips
                        groups={channelGroups}
                        selectedGroupId={selectedGroupId}
                        onSelectGroup={handleSelectGroup}
                    />
                }
                columns={channelOrderingColumnDefs}
                defaultColDef={{
                    sortable: false,
                }}
                gridOptions={{
                    pagination: false,
                    rowDragManaged: true,
                    animateRows: true,
                    onRowDragEnd,
                }}
                showAdd={false}
                pagination={false}
                showCaption={false}
                renderGridWhenEmpty
                entityForCaptionText="channels"
                footer={
                    <Footer
                        wrapperSx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                        disabled={!hasPendingChanges || saving}
                        onSave={onSave}
                        buttonId="save-channel-order-button"
                        buttonText="Save"
                    />
                }
            />
        </Box>
    );
};

export default ChannelOrderingTable;
